import {
  ID,
  QueryFilters,
  SingleApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import process from 'process';
import axios from 'axios';
import { stringify } from 'query-string';
import isEmpty from 'lodash/fp/isEmpty';
import { SentryService } from '@ianesthesie-kit/sentry';

export type WebPushSubscriptionPayload = {
  endpoint: string;
  p256dhKey: string;
  auth: string;
};

export type WebPushNotificationAckStatus = 'acknowledged' | 'dismissed';

export class PushNotificationService {
  private static INSTANCE: PushNotificationService;

  private readonly _url: string;

  private constructor() {
    this._url = '/bff/patient/web-push-notification';
  }

  private get available(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      !!this.serverKey
    );
  }

  private get serverKey(): string {
    return process.env.NEXT_PUBLIC_WEB_PUSH_PUBLIC_KEY ?? '';
  }

  public static getInstance() {
    if (!this.INSTANCE) {
      this.INSTANCE = new PushNotificationService();
    }
    return this.INSTANCE;
  }

  public serviceWorkerReadyAndActive(): Promise<boolean> {
    if (!this.available) {
      return Promise.resolve(false);
    }

    return navigator.serviceWorker.ready.then((reg) => !!reg.active);
  }

  public async currentDeviceSubscription(): Promise<PushSubscription | null> {
    if (!this.available) {
      return null;
    }
    return navigator.serviceWorker.ready.then(
      (se) => se?.pushManager?.getSubscription() ?? null
    );
  }

  public async currentPermissionStateAdjusted(): Promise<
    PermissionState | undefined
  > {
    let permission: PermissionState | undefined;
    try {
      permission = await this.currentPermissionState();
    } catch (err: any) {
      if (err?.name !== TypeError.name) {
        SentryService.getInstance().captureException(err);
      }
      return undefined;
    }

    if (permission === 'denied') {
      return 'denied';
    }

    const activeSub = await this.currentDeviceSubscription();
    const activeSubSavedUnderThisUser =
      !!activeSub && (await this.isSubscriptionSaved(activeSub));

    if (activeSub && !activeSubSavedUnderThisUser) {
      // if we reach this point, that means that this device is subscribed under another user
      // so we should delete the existing subscription and redo the process
      await this.unsubscribe();
      return 'prompt';
    }

    if (permission === 'granted' && activeSubSavedUnderThisUser) {
      return 'granted';
    }

    return 'prompt';
  }

  public async currentSubscriptionAdjusted(): Promise<PushSubscription | null> {
    const deviceSubscription = await this.currentDeviceSubscription();

    if (
      deviceSubscription &&
      (await this.isSubscriptionSaved(deviceSubscription))
    ) {
      return deviceSubscription;
    }

    return null;
  }

  public async unsubscribe() {
    if (!this.available) {
      return;
    }

    const sub = await this.currentDeviceSubscription();
    if (sub) {
      await sub.unsubscribe();
      await this.removeSavedSubscription(sub);
    }
  }

  public async subscribeIfNotSubscribed(): Promise<void> {
    if (!this.available) {
      return;
    }

    const se = await navigator.serviceWorker.ready;
    const sub = await se.pushManager?.getSubscription();
    // if there is no subscription at all for this device, ask for permission
    if (!sub) {
      // ask for permission
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const subscription = await se.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.serverKey
        });

        await this.saveSubscription(subscription);
      } else if (permission === 'denied') {
        // TODO handle
      }
    }
    // if subscription already exists for this device
    else if (sub) {
      // check if subscription has been persisted AND subscription is associated with current user
      const isPersisted = await this.isSubscriptionSaved(sub);
      if (!isPersisted) {
        await this.saveSubscription(sub);
      }
    }
  }

  public updateAckStatus(
    status: WebPushNotificationAckStatus,
    notification: Notification | ID
  ): Promise<void> {
    const id =
      typeof notification === 'string' ? notification : notification.data.id;
    if (!id) {
      const err = new Error('Notification has no id');
      SentryService.getInstance()
        .addBreadcrumb({
          level: 'error',
          message: 'Web push notification has no id',
          data: JSON.parse(JSON.stringify(notification))
        })
        .captureException(err);
      throw err;
    }

    let ack: boolean;
    if (status === 'acknowledged') {
      ack = true;
    } else if (status === 'dismissed') {
      ack = false;
    } else {
      const err = new Error(`Invalid status argument: ${status}`);
      SentryService.getInstance().captureException(err);
      throw err;
    }

    return axios.put(this.url(`${id}/status`, { ack })).then();
  }

  private isSubscriptionSaved(
    subscription: PushSubscription
  ): Promise<boolean> {
    return axios
      .get<SingleApiResponse<boolean>>(
        this.url('/subscription', { endpoint: subscription.endpoint })
      )
      .then((resp) => resp.data.data);
  }

  private saveSubscription(subscription: PushSubscription): Promise<void> {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys) {
      return Promise.reject(new Error('Invalid arg'));
    }
    return axios
      .post<SuccessApiResponse, void, WebPushSubscriptionPayload>(
        this.url('/subscription'),
        {
          endpoint: json.endpoint,
          auth: json.keys.auth,
          p256dhKey: json.keys.p256dh
        }
      )
      .then();
  }

  private removeSavedSubscription(
    subscription: PushSubscription
  ): Promise<void> {
    return axios
      .delete<SuccessApiResponse>(
        this.url('/subscription', { endpoint: subscription.endpoint })
      )
      .then();
  }

  private url(seg?: string, params?: QueryFilters) {
    if (!params && !seg) {
      return this._url;
    }
    let path = this._url;
    if (seg) {
      path += `/${seg}`;
      path = path.replaceAll('//', '/');
    }
    if (!isEmpty(params)) {
      path += `?${stringify(params)}`;
    }
    return path;
  }

  private async currentPermissionState() {
    if (!this.available) {
      return;
    }
    return navigator.serviceWorker.ready.then((se) =>
      se.pushManager.permissionState({
        userVisibleOnly: true
      })
    );
  }
}
